/**
 * THE `?tab=` VOCABULARY OF EVERY SURFACE THAT HAS TABS — the ids a surface
 * answers to, and the ids it USED to answer to.
 *
 * WHY THIS EXISTS BEFORE THE PAGES DO. A tab id used to be an address: it is
 * in somebody's bookmarks, on a printed procedure beside the POS, in a support
 * note, in the audit trail. The POC has been in use long enough to have
 * retired several, and every one of them is still honoured on arrival. If
 * this repo's surfaces were built first and their vocabularies second, the
 * first release would land on the default section for every address the POC's
 * users already hold. Resolving them in `validateSearch` means an old
 * `/settings?tab=branches` still opens Pharmacy today.
 *
 * THE ADDRESS DOES NOT KEEP THE ID (Rachel, 22-10-2026 — `app/module-address.ts`).
 * A module's address is `/<module>` and nothing after it. This file says which
 * section an old `?tab=` means. The route reads that once and replaces the
 * address with the bare module. A press on a section does not write `?tab=`.
 *
 * A RETIRED ID IS AN ALIAS, NEVER A DELETION. That is the POC's rule for every
 * surface, and this file is where it is kept: an alias maps onto the tab that
 * absorbed it. The address does not keep the id (`app/module-address.ts`): the
 * route reads the resolved section once, then replaces the address with the
 * bare module.
 *
 * AN UNKNOWN ID IS PASSED THROUGH, NOT REFUSED, and that is deliberate.
 * Settings and Reports take tabs that MODULES contribute through a registry
 * (POC `settings/contributions`, `reports/tabs`), so this core list cannot
 * tell a module's tab from a typo — and refusing it here would break a
 * module's bookmark before the module is built. The surface decides what an
 * id it has no tab for draws, when the surface exists. What IS refused is an
 * id that could not be one: anything outside `[a-z0-9-]`.
 *
 * IN `app/`, BESIDE `nav.ts`, and not in `features/<surface>/`. There is no
 * feature package for any of these surfaces yet, and the search palette will
 * read this list from the shell — which lint forbids reaching into a feature.
 * When a surface's package is born, its vocabulary moves into it and this file
 * re-exports it, so no route's import changes. `DECISIONS.md` 21-09-2026.
 *
 * Ids and aliases read off POC `main` @ dfdcc6e: `core/settings/sections.ts`,
 * `core/reports/tabs.ts`, `core/inventory/inventory-hub.tsx`; the catalogue's
 * split from Inventory off `core/catalogue/catalogue-hub.tsx` and
 * `shell/module-registry.ts` at `51669aa`. Both watermarks are
 * `docs/poc-watermarks.json ▸ tab-vocabulary`.
 *
 * ONLY WHAT THE SIMPLE EDITION DRAWS (`CLAUDE.md` ▸ Scope). The MVP is Simple
 * retail, so Booking has no vocabulary here, Reports has no Overview and
 * Inventory has no Counts. Nothing out of scope is built, and an id for it
 * would be an address that promises a screen.
 */
import { z } from "zod/mini";

export interface TabVocabulary {
  /** The core tabs, in the order the strip draws them. */
  tabs: readonly string[];
  /** Retired id → the tab that absorbed it. */
  aliases: Readonly<Record<string, string>>;
}

export const TABS = {
  settings: {
    tabs: ["pharmacy", "staff", "access", "log"],
    aliases: {
      // The tab that listed the branches was folded into Pharmacy & branches.
      branches: "pharmacy",
      // Asked two questions (language, density), both answered elsewhere now.
      preferences: "pharmacy",
      // Existed for one release, while the layout was the terminal's. It is the
      // reader's now and lives in the top bar — but an address that has been in
      // a URL resolves, even a short-lived one.
      terminal: "pharmacy",
    },
  },
  reports: {
    tabs: ["money", "inventory", "books", "audit"],
    aliases: {},
  },
  inventory: {
    tabs: ["stock", "suppliers", "supply-items", "procurement", "receipts", "adjustments", "movements"],
    aliases: {
      // Returns and corrections became one Adjustments tab.
      waste: "adjustments",
      // The shelf's day-end is gone — every supply moves the shelf when it is
      // made — and what survives of it (a line that could not be shelved) is
      // drawn on Stock. It was opened every evening, so it is in bookmarks.
      close: "stock",
    },
  },
  catalogue: {
    // What the branch may stock, and what it is bought and sold for. Two of
    // Inventory's tabs until the POC made them a surface of their own — see
    // `MOVED_TO_CATALOGUE` for the move, and the nav row for where the door is.
    tabs: ["products", "pricing"],
    aliases: {
      // The product list stopped being only medicines (the branch stocks masks,
      // a thermometer and two supplements), so the screen was renamed and the
      // id moved with the word. It is retired TWICE over now — renamed, then
      // moved to another surface — and it still resolves, because an address
      // somebody holds does not stop being theirs for having been retired
      // twice.
      formulary: "products",
    },
  },
} as const satisfies Record<string, TabVocabulary>;

/**
 * THE IDS THAT LEFT INVENTORY, and the surface that has them now.
 *
 * NOT `aliases`, and the difference is the whole point of the two mechanisms:
 * an alias resolves an old id to a tab the SAME surface still draws, and these
 * three name a screen that is no longer on this one. Resolving `pricing` inside
 * Inventory's vocabulary would answer a live address with whatever Inventory
 * draws first, which is the shelf — the right kind of wrong to never notice,
 * because the screen renders perfectly.
 *
 * `formulary` is in both maps on purpose and they do not disagree: this one
 * says which SURFACE answers it, `catalogue.aliases` says which TAB. An address
 * that has been retired twice resolves twice.
 *
 * Read by the Inventory route, which redirects rather than rendering. The hop
 * lands on `/catalogue` with nothing after it (`DECISIONS.md`, 22-10-2026).
 * This surface's search models `tab` alone, so a product a link named is not
 * on the address; when the catalogue screen arrives, that fact has to ride
 * in memory.
 */
export const MOVED_TO_CATALOGUE: Readonly<Record<string, string>> = {
  products: "products",
  pricing: "pricing",
  formulary: "products",
};

export type TabbedSurface = keyof typeof TABS;

const TAB_ID = /^[a-z0-9][a-z0-9-]*$/;

/**
 * The tab an address means. Absent stays absent — the surface draws its own
 * first tab, and an address without `?tab=` is not rewritten to carry one.
 */
export function resolveTab(surface: TabbedSurface, requested: unknown): string | undefined {
  if (typeof requested !== "string") return undefined;
  const id = requested.trim().toLowerCase();
  if (!TAB_ID.test(id)) return undefined;
  const aliases: Readonly<Record<string, string>> = TABS[surface].aliases;
  return aliases[id] ?? id;
}

/** `validateSearch` for a surface whose only search state is its tab. */
export function tabSearch(surface: TabbedSurface) {
  return z.object({
    tab: z.pipe(
      z.optional(z.unknown()),
      z.transform((value) => resolveTab(surface, value)),
    ),
  });
}
